import { type ReactNode, useCallback, useRef, useState } from 'react';

// #95: verstellbares Seiten-Panel. Breite = persönliche Ansichts-Präferenz → localStorage
// (überlebt Sitzungen; NICHT URL — Klärungsprotokoll 2026-08-17). Zieh-Griff mit Pflicht-
// Tastatur-Zwilling (Pfeiltasten, Regel aus #43: jede Zieh-Aktion braucht einen Zwilling).
// Doppelklick = Standardbreite (löscht den gespeicherten Wert, statt den Default zu speichern —
// so profitiert der Nutzer von einer späteren Default-Änderung).
const STEP = 16;

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

export function ResizablePanel({
  storageKey,
  defaultWidth,
  minWidth,
  maxWidth,
  ariaLabel,
  children,
}: {
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  ariaLabel: string;
  children: ReactNode;
}) {
  const [width, setWidth] = useState<number>(() => {
    const stored = Number(localStorage.getItem(storageKey));
    return Number.isFinite(stored) && stored > 0 ? clamp(stored, minWidth, maxWidth) : defaultWidth;
  });
  const dragStart = useRef<{ x: number; width: number } | null>(null);

  const apply = useCallback(
    (next: number, pointerX?: number) => {
      const clamped = clamp(next, minWidth, maxWidth);
      setWidth(clamped);
      localStorage.setItem(storageKey, String(clamped));
      // Review-Fix #95: dragStart bei jeder Änderung nachziehen — sonst überschreibt
      // der nächste pointermove einen Tastatur-Schritt, der während eines gehaltenen
      // Drags gesetzt wurde (dragStart.current.width wäre sonst weiter der beim
      // pointerdown eingefrorene Wert). Bei einem Tastatur-Schritt (kein pointerX) bleibt
      // die x-Referenz unverändert, nur die width-Basis zieht nach.
      if (dragStart.current) {
        dragStart.current = { x: pointerX ?? dragStart.current.x, width: clamped };
      }
    },
    [storageKey, minWidth, maxWidth],
  );

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragStart.current = { x: e.clientX, width };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    apply(dragStart.current.width + (e.clientX - dragStart.current.x), e.clientX);
  };
  const onPointerUp = () => {
    dragStart.current = null;
  };

  return (
    <div
      style={{ width }}
      className="relative flex shrink-0 flex-col border-r border-[var(--border)]"
    >
      {children}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={ariaLabel}
        aria-valuenow={width}
        aria-valuemin={minWidth}
        aria-valuemax={maxWidth}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onDoubleClick={() => {
          setWidth(defaultWidth);
          localStorage.removeItem(storageKey);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') apply(width + STEP);
          if (e.key === 'ArrowLeft') apply(width - STEP);
        }}
        className="absolute -right-[3px] top-0 z-10 h-full w-[6px] cursor-col-resize focus-visible:bg-brand-accent/40 hover:bg-brand-accent/20"
      />
    </div>
  );
}
